import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api, { apiError } from "../lib/api";
import { fmtUSD, fmtDate } from "../lib/format";
import { PageHeader, StatusBadge } from "../components/PageHeader";
import { DataTable } from "../components/DataTable";
import { Loader, KpiCard } from "../components/KpiCard";
import { Modal, Field, TextInput, SelectInput, AddButton } from "../components/Form";
import { HandCoins, CheckCircle2 } from "lucide-react";

const RTYPES = ["Individual Donation", "Corporate", "Grant", "Program Service", "In-Kind"];
const RACC = { "Individual Donation": "4010", "Corporate": "4020", "Grant": "4030", "Program Service": "4040", "In-Kind": "4050" };

export default function Revenue() {
  const [rows, setRows] = useState(null);
  const [donors, setDonors] = useState([]);
  const [funds, setFunds] = useState([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ revenue_date: new Date().toISOString().slice(0, 10), revenue_type: "Individual Donation", donor_id: "", fund_id: "", amount: "", payment_method: "Check" });

  const load = useCallback(() => {
    api.get("/revenue").then((r) => setRows(r.data));
    api.get("/donors").then((r) => setDonors(r.data));
    api.get("/funds").then((r) => setFunds(r.data));
  }, []);
  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/revenue", {
        ...form, amount: Number(form.amount), account_number: RACC[form.revenue_type],
        donor_id: form.donor_id || null, fund_id: form.fund_id || null, restricted: form.revenue_type === "Grant",
      });
      toast.success("Revenue recorded & posted to ledger");
      setOpen(false);
      setForm({ ...form, amount: "", donor_id: "" });
      load();
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };

  const acknowledge = async (id) => {
    try { await api.patch(`/revenue/${id}/acknowledge`); toast.success("Acknowledged"); load(); }
    catch (err) { toast.error(apiError(err)); }
  };

  if (!rows) return <Loader />;
  const total = rows.reduce((s, r) => s + r.amount, 0);
  const pending = rows.filter((r) => !r.acknowledged).length;

  const columns = [
    { key: "revenue_date", label: "Date", render: (r) => fmtDate(r.revenue_date) },
    { key: "donor_name", label: "Donor / Source" },
    { key: "revenue_type", label: "Type", render: (r) => <StatusBadge tone="blue">{r.revenue_type}</StatusBadge> },
    { key: "payment_method", label: "Method", className: "text-[#5C5C54]" },
    { key: "amount", label: "Amount", align: "right", render: (r) => <span className="text-inflow font-semibold">{fmtUSD(r.amount)}</span> },
    { key: "acknowledged", label: "Ack.", render: (r) => r.acknowledged
      ? <StatusBadge tone="green">Sent</StatusBadge>
      : <button onClick={(e) => { e.stopPropagation(); acknowledge(r.id); }} data-testid={`ack-${r.id}`}
          className="text-xs text-forest hover:underline">Acknowledge</button> },
  ];

  return (
    <div>
      <PageHeader title="Revenue & Donations" subtitle="Record contributions, grants and program income — auto-posted via double entry."
        action={<AddButton onClick={() => setOpen(true)} label="Record Revenue" testid="add-revenue-btn" />} />
      <div className="grid grid-cols-3 gap-6 mb-6">
        <KpiCard label="Total Revenue" value={fmtUSD(total)} icon={HandCoins} accent="#2E7D32" />
        <KpiCard label="Gifts Recorded" value={rows.length} icon={CheckCircle2} />
        <KpiCard label="Pending Acknowledgment" value={pending} accent="#B45309" />
      </div>
      <DataTable columns={columns} rows={rows} testid="revenue-table" />

      <Modal open={open} onOpenChange={setOpen} title="Record Revenue" onSubmit={submit} busy={busy} testid="revenue-modal">
        <Field label="Date"><TextInput type="date" required value={form.revenue_date} onChange={(e) => setForm({ ...form, revenue_date: e.target.value })} /></Field>
        <Field label="Type"><SelectInput value={form.revenue_type} onChange={(e) => setForm({ ...form, revenue_type: e.target.value })} options={RTYPES} /></Field>
        <Field label="Donor"><SelectInput value={form.donor_id} onChange={(e) => setForm({ ...form, donor_id: e.target.value })}
          options={[{ value: "", label: "— None —" }, ...donors.map((d) => ({ value: d.id, label: d.organization_name || `${d.first_name} ${d.last_name}` }))]} /></Field>
        <Field label="Fund"><SelectInput value={form.fund_id} onChange={(e) => setForm({ ...form, fund_id: e.target.value })}
          options={[{ value: "", label: "— None —" }, ...funds.map((f) => ({ value: f.id, label: f.fund_name }))]} /></Field>
        <Field label="Amount (USD)"><TextInput type="number" step="0.01" required value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} /></Field>
        <Field label="Payment Method"><SelectInput value={form.payment_method} onChange={(e) => setForm({ ...form, payment_method: e.target.value })} options={["Check", "ACH", "Credit Card", "Cash", "Wire"]} /></Field>
      </Modal>
    </div>
  );
}
